import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Loader2, Users, Table2, Download, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "../../lib/api";

function downloadCSV(data, filename) {
  const csv = [Object.keys(data[0]).join(","), ...data.map(r => Object.values(r).map(v => typeof v === "string" && v.includes(",") ? `"${v}"` : v).join(","))].join("\n");
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" })), download: filename });
  a.click(); URL.revokeObjectURL(a.href);
}

const MEDAL = ["🥇", "🥈", "🥉"];

export function TeamTablesTab({ restaurantId, startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/analytics/team-tables?${new URLSearchParams({ ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [restaurantId, startDate, endDate]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { seller_ranking, table_ranking, has_seller_data, has_table_data } = data;
  const maxSellerRev = Math.max(...(seller_ranking.map(s => s.revenue)), 1);
  const maxTableRev = Math.max(...(table_ranking.map(t => t.revenue)), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seller ranking */}
        <Card data-testid="seller-ranking">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" />Ranking de vendedores</CardTitle>
              {has_seller_data && (
                <Button variant="ghost" size="sm" onClick={() => downloadCSV(seller_ranking, "vendedores.csv")} className="h-7 text-xs gap-1">
                  <Download className="w-3 h-3" />CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!has_seller_data ? (
              <p className="text-sm text-muted-foreground text-center py-6">Sin datos de vendedor en este período</p>
            ) : (
              <div className="space-y-2">
                {seller_ranking.map((s, i) => (
                  <div key={s.seller} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{MEDAL[i] || `#${i + 1}`}</span>
                        <span className="font-medium">Vendedor {s.seller}</span>
                        <Badge variant="outline" className="text-xs">{s.transactions} tickets</Badge>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{s.revenue.toFixed(2)}€</span>
                        <span className="text-xs text-muted-foreground ml-1">({s.pct}%)</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(s.revenue / maxSellerRev) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table ranking */}
        <Card data-testid="table-ranking">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Table2 className="w-4 h-4" />Mesas por ingresos</CardTitle>
              {has_table_data && (
                <Button variant="ghost" size="sm" onClick={() => downloadCSV(table_ranking, "mesas.csv")} className="h-7 text-xs gap-1">
                  <Download className="w-3 h-3" />CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!has_table_data ? (
              <p className="text-sm text-muted-foreground text-center py-6">Sin datos de mesa en este período</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {table_ranking.map((t, i) => (
                  <div key={t.table} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      {t.table}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary/70" style={{ width: `${(t.revenue / maxTableRev) * 100}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.unique_tickets} tickets · tíc. medio {t.avg_ticket.toFixed(2)}€</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold">{t.revenue.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground">{t.pct}%</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bar charts */}
      {has_seller_data && seller_ranking.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ventas por vendedor</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={seller_ranking.slice(0, 8)} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="seller" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `V.${v}`} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} />
                <Tooltip formatter={v => [`${v}€`, "Ventas"]} labelFormatter={l => `Vendedor ${l}`} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                  {seller_ranking.slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "hsl(var(--primary))" : "hsl(var(--primary) / 0.5)"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
